import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, filter, map, timer } from 'rxjs';
import { Identifier } from '../models/storage.model';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService implements OnDestroy {
  public static readonly TYPE_API_KEY: string = 'apiKey';
  public static readonly TYPE_LANGUAGE: string = 'culture';

  private readonly subscription: Subscription = new Subscription();
  private storageSubjectsMap: Map<string, BehaviorSubject<any>>;
  private storageValues: any = {};

  constructor() {
    this.storageSubjectsMap = new Map<string, BehaviorSubject<any>>();
    this.checkLocalStorageChanges();
  }

  checkLocalStorageChanges() {
    const checkLocalStoreChangesTimer$ = timer(10000, 5000);
    this.subscription.add(
      checkLocalStoreChangesTimer$.subscribe(() => {
        for (const [key, value] of this.storageSubjectsMap.entries()) {
          const storageItem = localStorage.getItem(key);
          if (this.storageValues[key] !== storageItem) {
            this.storageValues[key] = storageItem;
            value.next(storageItem);
          }
        }
      })
    );
  }

  private getStorageSubject(identifier: string): BehaviorSubject<any> {
    let storageSubject: BehaviorSubject<any> | undefined = this.storageSubjectsMap.get(identifier);
    if (!storageSubject) {
      storageSubject = new BehaviorSubject<any>(null);
      this.storageSubjectsMap.set(identifier, storageSubject);
    }
    return storageSubject;
  }

  public getStorageItem(identifier: string): BehaviorSubject<any> {
    const storageItem = localStorage.getItem(identifier);
    const storageSubject = this.getStorageSubject(identifier);
    if (this.storageValues[identifier] !== storageItem) {
      this.storageValues[identifier] = storageItem;
      storageSubject.next(storageItem);
    }
    return storageSubject;
  }

  public getStorageModel<T>(identifier: Identifier): Observable<T> {
    return this.getStorageItem(identifier).pipe(
      filter((item): item is string => !!item),
      map((item: string) => JSON.parse(item))
    );
  }

  public setStorageItem(identifier: string, object: string): void {
    this.storageValues[identifier] = object;
    localStorage.setItem(identifier, object);
    this.getStorageSubject(identifier).next(object);
  }

  public removeStorageItem(identifier: string): void {
    localStorage.removeItem(identifier);
    this.getStorageSubject(identifier).next(null);
  }

  public clear() {
    localStorage.clear();
    this.storageSubjectsMap.forEach((storageSubject: BehaviorSubject<any>) => {
      storageSubject.next(null);
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
